/**
 * Postal Code Service
 * Fetches postal codes for cities using various APIs
 */

interface PostalCodeResult {
  postalCode: string;
  placeName?: string;
}

interface CityPostalCodesResponse {
  success: boolean;
  postalCodes: string[];
  city?: string;
  state?: string;
  country?: string;
  error?: string;
}

/**
 * Fetch postal codes for a US city using Zippopotam.us API
 */
async function fetchUSPostalCodes(city: string, state: string): Promise<CityPostalCodesResponse> {
  try {
    // Zippopotam.us API format: https://api.zippopotam.us/us/{state}/{city}
    // Note: city name should be URL encoded and state should be 2-letter code
    const stateCode = state.length === 2 ? state.toUpperCase() : state.toUpperCase();
    const cityEncoded = encodeURIComponent(city);
    
    const response = await fetch(`https://api.zippopotam.us/us/${stateCode}/${cityEncoded}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          postalCodes: [],
          city,
          state,
          country: 'US',
          error: `No postal codes found for ${city}, ${state}`
        };
      }
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.places && Array.isArray(data.places)) {
      const postalCodes = data.places.map((place: any) => place['post code'] || place.postCode).filter(Boolean);
      
      return {
        success: true,
        postalCodes: [...new Set(postalCodes)], // Remove duplicates
        city: data.places[0]?.['place name'] || city,
        state: data.state || state,
        country: 'US'
      };
    }
    
    return {
      success: false,
      postalCodes: [],
      city,
      state,
      country: 'US',
      error: 'Invalid response format'
    };
  } catch (error) {
    console.error('Error fetching US postal codes:', error);
    return {
      success: false,
      postalCodes: [],
      city,
      state,
      country: 'US',
      error: error instanceof Error ? error.message : 'Failed to fetch postal codes'
    };
  }
}

/**
 * Fetch postal codes for a Canadian city using Zippopotam.us API
 */
async function fetchCAPostalCodes(city: string, province: string): Promise<CityPostalCodesResponse> {
  try {
    // Zippopotam.us API format: https://api.zippopotam.us/ca/{province}/{city}
    // Province should be 2-letter code (e.g., ON, BC, QC)
    const provinceCode = province.length === 2 ? province.toUpperCase() : province.toUpperCase();
    const cityEncoded = encodeURIComponent(city);
    
    const response = await fetch(`https://api.zippopotam.us/ca/${provinceCode}/${cityEncoded}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          postalCodes: [],
          city,
          state: province,
          country: 'CA',
          error: `No postal codes found for ${city}, ${province}`
        };
      }
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.places && Array.isArray(data.places)) {
      const postalCodes = data.places.map((place: any) => place['post code'] || place.postCode).filter(Boolean);
      
      return {
        success: true,
        postalCodes: [...new Set(postalCodes)], // Remove duplicates
        city: data.places[0]?.['place name'] || city,
        state: data['state abbreviation'] || province,
        country: 'CA'
      };
    }
    
    return {
      success: false,
      postalCodes: [],
      city,
      state: province,
      country: 'CA',
      error: 'Invalid response format'
    };
  } catch (error) {
    console.error('Error fetching Canadian postal codes:', error);
    return {
      success: false,
      postalCodes: [],
      city,
      state: province,
      country: 'CA',
      error: error instanceof Error ? error.message : 'Failed to fetch postal codes'
    };
  }
}

/**
 * Main function to fetch postal codes by city
 * Supports US and Canada via Zippopotam.us (free, no API key required)
 */
export async function fetchPostalCodesByCity(
  city: string,
  stateOrProvince: string,
  country: 'US' | 'CA' = 'US'
): Promise<CityPostalCodesResponse> {
  if (!city || !stateOrProvince) {
    return {
      success: false,
      postalCodes: [],
      error: 'City and state/province are required'
    };
  }
  
  if (country === 'CA') {
    return fetchCAPostalCodes(city, stateOrProvince);
  } else {
    return fetchUSPostalCodes(city, stateOrProvince);
  }
}

/**
 * US State codes mapping (full name to 2-letter code)
 */
export const US_STATES: { [key: string]: string } = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC'
};

/**
 * Canadian Province codes mapping
 */
export const CANADIAN_PROVINCES: { [key: string]: string } = {
  'Alberta': 'AB', 'British Columbia': 'BC', 'Manitoba': 'MB', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Northwest Territories': 'NT', 'Nova Scotia': 'NS',
  'Nunavut': 'NU', 'Ontario': 'ON', 'Prince Edward Island': 'PE', 'Quebec': 'QC',
  'Saskatchewan': 'SK', 'Yukon': 'YT'
};

